/**
 * Divination Engine - Infers technician learning style preferences
 * from their training consumption behavior (Foundational only).
 * 
 * Also provides multi-signal completion confidence calculation
 * for the Training Intelligence System.
 */

// DTC-G G3: route the elapsed-time anchor in
// `calculateCompletionConfidence` through the canonical clock so
// suspicious/verified classification is deterministic under
// `clock.setFixed(...)`.
import { clock } from './clock';

export type LearningStyle = 'visual' | 'auditory' | 'read_write' | 'hands_on';
export type ConfidenceLevel = 'verified' | 'likely' | 'unverified' | 'suspicious';
export type CompletionStatus = 'completed' | 'partial' | 'abandoned' | 'pending' | 'skipped';

// Multi-signal confidence tier (Phase 2 addition)
export type ConfidenceTier = 'verified' | 'likely' | 'suspicious';

export interface ConsumptionSession {
  id: string;
  artifact_id: string;
  artifact_type: string;
  opened_at: string;
  confirmed_at: string | null;
  completion_status: CompletionStatus | null;
  completion_confidence: ConfidenceLevel;
  progress_percent: number | null;
  expected_duration_minutes: number | null;
  actual_time_spent_minutes: number | null;
}

export interface InferredStyleWeights {
  visual: number;
  auditory: number;
  read_write: number;
  hands_on: number;
}

export interface InferenceResult {
  inferredStyles: InferredStyleWeights;
  inferredConfidence: Record<LearningStyle, number>;
  dominantStyle: LearningStyle | null;
}

// =============================================
// MULTI-SIGNAL CONFIDENCE ENGINE (Phase 2)
// =============================================

export interface MultiSignalInputs {
  /** Time spent on training (minutes) */
  elapsedMinutes: number;
  /** Expected duration of training (minutes) */
  expectedMinutes: number;
  /** Score from reflection prompts (0-100) */
  reflectionScore: number | null;
  /** Whether a job was completed after this training */
  hasLinkedJobOutcome: boolean;
}

export interface MultiSignalResult {
  /** Confidence score 0-100 */
  confidence: number;
  /** Confidence tier for aggregation */
  tier: ConfidenceTier;
}

/**
 * Calculate multi-signal completion confidence
 * 
 * Weights:
 * - Time plausibility: 40%
 * - Reflection prompts: 30%
 * - Post-training job outcome: 30%
 * 
 * @param inputs - Multi-signal input data
 * @returns Confidence score and tier
 */
export function calculateMultiSignalConfidence(
  inputs: MultiSignalInputs
): MultiSignalResult {
  let score = 0;
  
  // Time plausibility (40% weight)
  const timeRatio = inputs.expectedMinutes > 0 
    ? inputs.elapsedMinutes / inputs.expectedMinutes 
    : 0;
  
  if (timeRatio >= 0.7) {
    score += 40;
  } else if (timeRatio >= 0.4) {
    score += 25;
  } else if (timeRatio >= 0.2) {
    score += 10;
  }
  // < 0.2 = suspicious, no points
  
  // Reflection prompts (30% weight)
  if (inputs.reflectionScore !== null && inputs.reflectionScore > 0) {
    score += (inputs.reflectionScore / 100) * 30;
  }
  
  // Post-training job outcome (30% weight)
  if (inputs.hasLinkedJobOutcome) {
    score += 30;
  }
  
  // Determine tier
  let tier: ConfidenceTier;
  if (score >= 70) {
    tier = 'verified';
  } else if (score >= 40) {
    tier = 'likely';
  } else {
    tier = 'suspicious';
  }
  
  return { confidence: score, tier };
}

/**
 * Get confidence tier weight for aggregation
 * Used in global metrics to weight completions by quality
 */
export function getConfidenceTierWeight(tier: ConfidenceTier): number {
  const weights: Record<ConfidenceTier, number> = {
    verified: 1.0,
    likely: 0.6,
    suspicious: 0.1,
  };
  return weights[tier];
}

/**
 * Map artifact types to learning styles
 */
export function mapArtifactTypeToStyle(artifactType: string): LearningStyle {
  const mapping: Record<string, LearningStyle> = {
    short_video: 'visual',
    long_video: 'visual',
    podcast: 'auditory',
    audiobook: 'auditory',
    book: 'read_write',
    ebook: 'read_write',
    article: 'read_write',
    whitepaper: 'read_write',
    hands_on_lab: 'hands_on',
    live_seminar: 'hands_on',
    workshop: 'hands_on',
    webinar: 'visual', // webinars are mostly visual
  };
  
  return mapping[artifactType] || 'visual';
}

/**
 * Calculate duration weight (longer content = more signal)
 */
function durationWeight(durationMinutes: number | null): number {
  if (!durationMinutes) return 1.0;
  if (durationMinutes <= 10) return 0.5;
  if (durationMinutes <= 30) return 1.0;
  if (durationMinutes <= 60) return 1.2;
  return 1.5; // long-form content (books, courses)
}

/**
 * Calculate confidence multiplier for consumption record
 */
function confidenceMultiplier(confidence: ConfidenceLevel): number {
  const multipliers: Record<ConfidenceLevel, number> = {
    verified: 1.0,
    likely: 0.7,
    unverified: 0.3,
    suspicious: 0,
  };
  return multipliers[confidence] ?? 0.3;
}

/**
 * Calculate weight for a single consumption session
 */
function calculateSessionWeight(session: ConsumptionSession): number {
  const baseWeight = (() => {
    switch (session.completion_status) {
      case 'completed':
        return 1.0;
      case 'partial': {
        const progress = session.progress_percent ?? 0;
        return 0.3 + (progress / 100) * 0.5; // 0.3 to 0.8 based on progress
      }
      case 'abandoned':
      case 'skipped':
        return -0.1;
      default:
        return 0;
    }
  })();
  
  return baseWeight 
    * confidenceMultiplier(session.completion_confidence)
    * durationWeight(session.expected_duration_minutes);
}

/**
 * Calculate inferred learning styles from consumption behavior
 */
export function calculateInferredStyles(
  sessions: ConsumptionSession[],
  explicitPreferences?: LearningStyle[]
): InferenceResult {
  const weights: InferredStyleWeights = {
    visual: 0,
    auditory: 0,
    read_write: 0,
    hands_on: 0,
  };
  
  const counts: Record<LearningStyle, number> = {
    visual: 0,
    auditory: 0,
    read_write: 0,
    hands_on: 0,
  };
  
  // Process each session
  for (const session of sessions) {
    if (!session.completion_status || session.completion_status === 'pending') {
      continue;
    }
    
    const style = mapArtifactTypeToStyle(session.artifact_type);
    const weight = calculateSessionWeight(session);
    
    weights[style] += weight;
    counts[style]++;
  }
  
  // Normalize weights to 0-1 range
  const maxWeight = Math.max(...Object.values(weights), 0.001);
  const normalizedWeights: InferredStyleWeights = {
    visual: Math.max(0, weights.visual / maxWeight),
    auditory: Math.max(0, weights.auditory / maxWeight),
    read_write: Math.max(0, weights.read_write / maxWeight),
    hands_on: Math.max(0, weights.hands_on / maxWeight),
  };
  
  // Calculate confidence per style (Bayesian-ish smoothing)
  // confidence = samples / (samples + 5)
  const inferredConfidence: Record<LearningStyle, number> = {
    visual: counts.visual / (counts.visual + 5),
    auditory: counts.auditory / (counts.auditory + 5),
    read_write: counts.read_write / (counts.read_write + 5),
    hands_on: counts.hands_on / (counts.hands_on + 5),
  };
  
  // Find dominant style (highest weight with reasonable confidence)
  let dominantStyle: LearningStyle | null = null;
  let maxScore = 0;
  
  for (const style of ['visual', 'auditory', 'read_write', 'hands_on'] as LearningStyle[]) {
    const score = normalizedWeights[style] * inferredConfidence[style];
    if (score > maxScore && counts[style] >= 2) {
      maxScore = score;
      dominantStyle = style;
    }
  }
  
  return {
    inferredStyles: normalizedWeights,
    inferredConfidence,
    dominantStyle,
  };
}

/**
 * `calculateCompletionConfidence` was REMOVED by CORE-AI Closure Slice 1.
 *
 * It derived `completion_confidence` in the BROWSER from wall-clock elapsed
 * time: ≥70% of the expected duration returned 'verified', <30% returned
 * 'suspicious'. `useConfirmConsumption` wrote that straight to
 * `training_consumption_sessions`, so the subject of the claim decided what
 * OptiBay had verified about them — from a clock, with no observation of any
 * kind. A person who opened a video and walked away got 'verified'; a fast
 * reader got 'suspicious'.
 *
 * The Constitution reserves that word: "verified completion" means only that
 * OptiBay holds sufficient GOVERNED evidence. The three authorities that may
 * now set the column are verify_training_completion() (an audited human
 * attestation), revoke_training_completion() and promote_extension_confidence()
 * (a measurement, capped at 'likely'), and
 * `20261127120000_core_ai_one_adjudication_authority.sql` makes that structural
 * rather than conventional.
 *
 * It is deleted rather than left unused: a function that returns 'verified'
 * from a clock is a defect waiting to be re-imported. `ConfidenceLevel` stays —
 * it is the shared vocabulary, not the heuristic.
 */

/**
 * Get style affinity boost for recommendation scoring
 * Returns a multiplier between 1.0 and 1.3
 */
export function getStyleAffinityBoost(
  artifactType: string,
  inferredStyles: InferredStyleWeights | null,
  explicitPreferences: LearningStyle[] | null
): number {
  if (!inferredStyles && (!explicitPreferences || explicitPreferences.length === 0)) {
    return 1.0;
  }
  
  const artifactStyle = mapArtifactTypeToStyle(artifactType);
  
  let boost = 1.0;
  
  // Inferred preference boost (up to +0.2)
  if (inferredStyles) {
    boost += inferredStyles[artifactStyle] * 0.2;
  }
  
  // Explicit preference boost (+0.1)
  if (explicitPreferences?.includes(artifactStyle)) {
    boost += 0.1;
  }
  
  return Math.min(1.3, boost);
}
